/**
 * Las citas del paciente: las que vienen y las que ya pasaron.
 *
 * Abre por las próximas porque esa es la pregunta ("¿cuándo me toca?"). El
 * historial está a un clic, para quien viene a comprobar cuándo fue la última
 * vez que lo vieron.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";
import { misCitas } from "../../api/portal";
import { usePortalAuth } from "../../auth/PortalAuthProvider";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { fechaHora } from "../../lib/fecha";
import { Badge, Card, CardBody } from "../../components/ui";

export function PortalCitasPage() {
  const [proximas, setProximas] = useState(true);
  const { me } = usePortalAuth();
  const tz = useZonaHoraria();

  const { data: citas = [], isLoading } = useQuery({
    queryKey: ["portal", "citas", proximas],
    queryFn: () => misCitas(proximas),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-slate-900">Mis citas</h1>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm">
          {[
            { id: true, label: "Próximas" },
            { id: false, label: "Anteriores" },
          ].map((op) => (
            <button
              key={String(op.id)}
              type="button"
              onClick={() => setProximas(op.id)}
              className={`rounded-md px-3 py-1.5 font-medium transition ${
                proximas === op.id
                  ? "bg-[color:var(--color-link)] text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {op.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className="text-sm text-slate-400">Cargando…</p>}

      {!isLoading && citas.length === 0 && (
        <Card>
          <CardBody className="py-10 text-center text-sm text-slate-500">
            {proximas
              ? "No tienes citas programadas. Para pedir una, llama o acércate a admisión."
              : "Todavía no tienes atenciones anteriores."}
          </CardBody>
        </Card>
      )}

      <div className="space-y-3">
        {citas.map((c) => (
          <Card key={c.id}>
            <CardBody className="flex flex-wrap items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)]">
                <CalendarDays className="h-5 w-5" />
              </span>
              <div className="min-w-0 grow">
                <p className="font-semibold text-slate-900">
                  {fechaHora(c.inicio, tz)}
                </p>
                <p className="text-sm text-slate-600">
                  {c.especialidad}
                  {c.profesional ? ` · ${c.profesional}` : ""}
                </p>
                {c.sede && <p className="text-sm text-slate-500">{c.sede}</p>}
                {/* Solo se nombra al paciente cuando NO es uno mismo: si no,
                    sería repetirle su nombre en cada tarjeta. */}
                {c.paciente_id !== me?.yo.paciente_id && (
                  <p className="mt-1">
                    <Badge variant="neutral">{c.paciente_nombre}</Badge>
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Badge variant={c.estado === "Atendida" ? "success" : "neutral"}>
                  {c.estado}
                </Badge>
                {proximas && !c.pagada && (
                  <span className="text-xs font-medium text-amber-700">
                    Pasa por caja
                  </span>
                )}
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}
